import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import { useTenantScope } from "../scope/TenantScopeContext";

const panel = {
  background: "#101923",
  border: "1px solid #263747",
  borderRadius: 14,
  padding: 20,
  marginBottom: 18,
};

function profile() {
  for (const key of ["user", "currentUser", "profile"]) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || "null");
      if (value && typeof value === "object") return value;
    } catch (_) {}
  }
  return {};
}

async function api(path) {
  const response = await apiFetch(path);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.detail || `HTTP ${response.status}`);
  }
  return body;
}

export default function AssetDetailsPage() {
  const { assetId } = useParams();
  const scope = useTenantScope();
  const user = useMemo(profile, []);
  const role = user.role || localStorage.getItem("role") || "";

  const companyId =
    role === "super_admin"
      ? String(scope.companyId || "")
      : String(user.company_id || localStorage.getItem("company_id") || "");

  const [inventory, setInventory] = useState(null);
  const [applications, setApplications] = useState([]);
  const [runtime, setRuntime] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!assetId) return;

    if (role === "super_admin" && !companyId) {
      setError("Select a company from the global scope control.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const suffix =
        role === "super_admin"
          ? `?company_id=${encodeURIComponent(companyId)}`
          : "";

      const separator = suffix ? "&" : "?";

      const [detail, apps, runtimeDetail] = await Promise.all([
        api(
          `/endpoint-inventory/devices/${encodeURIComponent(assetId)}${suffix}`
        ),
        api(
          `/endpoint-inventory/devices/${encodeURIComponent(
            assetId
          )}/applications${suffix}${separator}state=installed&limit=1000`
        ),
        api(
          `/runtime-telemetry/devices/${encodeURIComponent(assetId)}${suffix}`
        ).catch(() => null),
      ]);

      setInventory(detail);
      setApplications(Array.isArray(apps) ? apps : []);
      setRuntime(runtimeDetail);
    } catch (err) {
      setError(err.message);
      setInventory(null);
    } finally {
      setLoading(false);
    }
  }, [assetId, companyId, role]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <main style={{ color: "#eef5fb", padding: 24 }}>
        Loading endpoint asset...
      </main>
    );
  }

  return (
    <main
      style={{
        color: "#eef5fb",
        padding: 24,
        maxWidth: 1500,
        margin: "0 auto",
      }}
    >
      <p>
        <Link to="/assets/topology" style={{ color: "#9fd3ff" }}>
          ← Back to Asset Topology
        </Link>
      </p>

      {error && (
        <section
          style={{
            ...panel,
            borderColor: "#b42318",
            color: "#ffb4ab",
          }}
        >
          {error}
        </section>
      )}

      {inventory && (
        <>
          <h1>
            {inventory.device_name ||
              inventory.hostname ||
              inventory.device_uuid}
          </h1>

          <p style={{ color: "#a9bbca" }}>
            Canonical endpoint inventory and runtime telemetry detail.
          </p>

          <section style={panel}>
            <h2>Endpoint identity</h2>
            <p>
              <strong>UUID:</strong> {inventory.device_uuid}
            </p>
            <p>
              <strong>Lifecycle:</strong>{" "}
              {inventory.lifecycle_state || "Unknown"}
            </p>
            <p>
              <strong>Observed:</strong>{" "}
              {inventory.observed_at || "Not reported"}
            </p>
            <p>
              <strong>Collector:</strong>{" "}
              {inventory.collector_version || "Unknown"}
            </p>
          </section>

          <section style={panel}>
            <h2>Hardware</h2>
            <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {JSON.stringify(inventory.hardware || {}, null, 2)}
            </pre>
          </section>

          <section style={panel}>
            <h2>Operating system</h2>
            <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {JSON.stringify(inventory.os || {}, null, 2)}
            </pre>
          </section>

          <section style={panel}>
            <h2>Installed applications ({applications.length})</h2>

            <div style={{ overflowX: "auto", maxHeight: 520 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th align="left">Name</th>
                    <th align="left">Version</th>
                    <th align="left">Publisher</th>
                    <th align="left">Architecture</th>
                  </tr>
                </thead>

                <tbody>
                  {applications.map((item) => (
                    <tr key={item.application_uuid}>
                      <td>{item.name}</td>
                      <td>{item.version || "—"}</td>
                      <td>{item.publisher || "—"}</td>
                      <td>{item.architecture || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section style={panel}>
            <h2>Runtime telemetry</h2>

            {!runtime ? (
              <p>No runtime telemetry is currently available for this endpoint.</p>
            ) : (
              <>
                <p>
                  Observed {runtime.observed_at || "Unknown"}
                </p>

                <p>
                  Processes: {(runtime.processes || []).length} · Services:{" "}
                  {(runtime.services || []).length} · Connections:{" "}
                  {(runtime.network_connections || []).length}
                </p>

                <h3>Firewall state</h3>

                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {JSON.stringify(runtime.firewall_state || {}, null, 2)}
                </pre>
              </>
            )}
          </section>
        </>
      )}
    </main>
  );
}
